import * as React from 'react';

import type { TableColumnDefinition, TableDefinitions, TableView } from '@gitbook/doc-core';
import { table } from '@gitbook/doc-core';
import { PaletteButton } from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';

import { useContentReference } from '../../../ContentReferences';
import { useInsertFiles } from '../../../InsertFilesDialog';
import { definitionSpecs } from '../../table/ColumnDefinitions';
import type { TableRecordKV } from '../../table/types';
import { useCardFieldOptions } from '../useCardFieldOptions';

interface CardFieldOptionsProps {
    /** Key of the parent Table block */
    nodeKey: string;

    /** Editor instance */
    editor: EditorContainer;

    /** Current view in the table */
    view: TableView;

    /** Table definition */
    tableDef: TableDefinitions;

    /** Column definition of the column */
    columnDefinition: TableColumnDefinition;

    /** The record for the current field */
    record: TableRecordKV;
}

/**
 * Inline button within a card field, to edit both the field and the column-field options.
 */
export function CardFieldOptions(
    props: CardFieldOptionsProps & Pick<ButtonProps, 'className' | 'leadingIcon' | 'trailingIcon'>
): React.ReactElement {
    const { nodeKey, editor, view, columnDefinition, record } = props;

    // Get insertFiles function for file fields
    const insertFiles = useInsertFiles();

    // State to store resolved file data
    const [resolvedFiles, setResolvedFiles] = React.useState<
        Array<{ fileId: string; name: string; href?: string }>
    >([]);

    // Resolve file data for file fields
    const fileIds = React.useMemo(() => {
        if (columnDefinition.type !== 'files') return [];

        const value = record.value.values[columnDefinition.id] as string[];
        return Array.isArray(value) ? value : [];
    }, [columnDefinition.type, columnDefinition.id, record.value.values]);

    // Handle resolved file data
    const handleFileResolved = React.useCallback(
        (fileData: { fileId: string; name: string; href?: string }) => {
            setResolvedFiles((prev) => {
                const existing = prev.find((f) => f.fileId === fileData.fileId);
                if (existing) {
                    return prev.map((f) => (f.fileId === fileData.fileId ? fileData : f));
                }
                return [...prev, fileData];
            });
        },
        []
    );

    const { createPaletteCommands } = useCardFieldOptions({
        nodeKey,
        editor,
        view,
        columnDefinition,
        record,
        resolvedFiles: columnDefinition.type === 'files' ? resolvedFiles : undefined,
        insertFiles: columnDefinition.type === 'files' ? insertFiles : undefined,
        onValueChange: async (newValue) => {
            editor.change((change) => {
                table.setRecordValue(change, nodeKey, record.key, columnDefinition.id, newValue);
            });
        },
    });

    return (
        <>
            {/* Render file resolvers for file fields */}
            {columnDefinition.type === 'files' &&
                fileIds.map((fileId) => (
                    <FileResolver key={fileId} fileId={fileId} onResolved={handleFileResolved} />
                ))}

            <PaletteButton
                kind="secondary"
                size="small"
                leadingIcon={Icon.MoreVertical}
                palette={createPaletteCommands}
                iconOnly
                label={definitionSpecs[columnDefinition.type].title}
                title="Field options"
                className="absolute top-0 right-0 w-fit px-0 py-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/record-value:opacity-100"
            />
        </>
    );
}

/**
 * Component that resolves a single file and provides its data
 */
function FileResolver({
    fileId,
    onResolved,
}: {
    fileId: string;
    onResolved: (data: { fileId: string; name: string; href?: string }) => void;
}) {
    const resolved = useContentReference({ kind: 'file', file: fileId });

    React.useEffect(() => {
        onResolved({
            fileId,
            name: resolved.resolution?.extra?.file?.name || 'Deleted file',
            href: resolved.href,
        });
    }, [fileId, resolved.resolution?.extra?.file?.name, resolved.href, onResolved]);

    return null;
}
